import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RichText, Text } from "@sitecore-jss/sitecore-jss-react";

import classNames from "classnames";
import { useActiveProductData, useActiveModelData, useActiveConfigurationSKU } from "@store/pdp";
import useGlobalStore, { useDeliveryTextsFallback } from "@store/global";
import useStockStore from "@store/stock";
import parseSpecifications from "@assets/js/parse-specifications";
import { log } from "@assets/js/utils";
import getUniqueId from "@assets/js/get-unique-id";
import getFormattedDeliveryTime from "@assets/js/get-formatted-delivery-time"

import "./pdp-vertical-product-specification.scss";

const PDPVerticalProductSpecification = ({
    deliveryTexts,
    defaultDimensionUnit = "Metric",
    careAndMaintenance = "<a></a>",
}) => {
    const { t } = useTranslation();
    const texts = {
        downloads: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Downloads"),
        about: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_About"),
        specifications: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Specifications"),
        careAndMaintenance: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_CareAndMaintenance"),
        deliveryAndReturn: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_DeliveryAndReturn"),
        aboutTheDesigner: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_AboutTheDesigner"),
        aboutTheProduct: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_AboutTheProduct"),
        dimensionsMetric: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_DimensionsMetric"),
        dimensionsImperial: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_DimensionsImperial"),
        noDelivery: t("FritzHansen_Project_ConsumersAndProfessionals_Cart_NoDelivery"),
    };

    const stockList = useStockStore((state) => state.stockList);
    const [selectedSpecification, setSelectedSpecification] = useState(SPECIFICATION.ABOUT);
    const [activeProductData] = useActiveProductData();
    const [activeModelData] = useActiveModelData();
    const [activeConfigurationSKU] = useActiveConfigurationSKU();
    const [activeDimensionFilter, setActiveDimensionFilter] = useState(defaultDimensionUnit);
    const [deliveryAndReturnText, setDeliveryAndReturnText] = useState(null);
    const [specAccordions, setSpecAccordions] = useState([]);
    const [dimensionSpecs, setDimensionSpecs] = useState([]);
    const [commerceMarket, language] = useGlobalStore((state) => [state.commerceMarket, state.language]);
    const [deliveryTextsFallback] = useDeliveryTextsFallback();

    useEffect(() => {
        if (activeModelData?.specifications) {
            let s = parseSpecifications(
                activeModelData.specifications,
                language,
                true
            );

            let nonDimensionSpecs = s.filter(x => x?.value[0]?.sectionId !== "Dimensions" && x?.value[0]?.sectionId !== "ImperialDimensions");
            setSpecAccordions(nonDimensionSpecs);

            let dimensionSpecs = s.filter(x => x?.value[0]?.sectionId === "Dimensions" || x?.value[0]?.sectionId === "ImperialDimensions");
            setDimensionSpecs(dimensionSpecs);
        }
    }, [activeModelData, language]);

    useEffect(() => {
        if(activeConfigurationSKU && activeModelData && activeModelData.deliveryKey && deliveryTexts && deliveryTexts[activeModelData.deliveryKey]){
            let deliveryAndReturnText = deliveryTexts[activeModelData.deliveryKey];
            if (stockList && language && commerceMarket) {
                deliveryAndReturnText = deliveryAndReturnText.replace('{modelType}', activeConfigurationSKU);
                let fallbackText = texts.noDelivery;
                if (deliveryTextsFallback) {
                    fallbackText = deliveryTextsFallback[commerceMarket];
                    if (!fallbackText) {
                        fallbackText = deliveryTextsFallback["dk"];
                    }
                }
                let formattedDeliveryTime = getFormattedDeliveryTime(stockList, activeConfigurationSKU, fallbackText, language, commerceMarket.toUpperCase());
                deliveryAndReturnText = deliveryAndReturnText.replace('{deliveryDateText}', formattedDeliveryTime);
            }
            setDeliveryAndReturnText(deliveryAndReturnText);
        }
    }, [activeProductData, activeModelData, stockList, language, commerceMarket]);

    return <div className="pdp-vertical-product-specification" id="verticalProductSpecifications">
        <div className="specification-list-filters">
            <div className="specification-list-filters__inner">
                <ul>
                    <PDPVerticalProductSpecificationListItem
                        key={`VerticalProductSpecificationListItem${SPECIFICATION.ABOUT}`}
                        title={texts.about}
                        isActive={selectedSpecification === SPECIFICATION.ABOUT}
                        clickHandler={() => { setSelectedSpecification(SPECIFICATION.ABOUT) }} />
                    <PDPVerticalProductSpecificationListItem
                        key={`VerticalProductSpecificationListItem${SPECIFICATION.SPECIFICATIONS}`}
                        title={texts.specifications}
                        isActive={selectedSpecification === SPECIFICATION.SPECIFICATIONS}
                        clickHandler={() => { setSelectedSpecification(SPECIFICATION.SPECIFICATIONS) }} />
                    {/* <PDPVerticalProductSpecificationListItem
                        key={`VerticalProductSpecificationListItem${SPECIFICATION.CERTIFICATES}`}
                        title={"Certificates"}
                        isActive={selectedSpecification === SPECIFICATION.CERTIFICATES}
                        clickHandler={() => { setSelectedSpecification(SPECIFICATION.CERTIFICATES) }} /> */}
                    <PDPVerticalProductSpecificationListItem
                        key={`VerticalProductSpecificationListItem${SPECIFICATION.DOWNLOADS}`}
                        title={texts.downloads}
                        isActive={selectedSpecification === SPECIFICATION.DOWNLOADS}
                        clickHandler={() => { setSelectedSpecification(SPECIFICATION.DOWNLOADS) }} />
                    {/* <PDPVerticalProductSpecificationListItem
                        key={`VerticalProductSpecificationListItem${SPECIFICATION.WARRANTY}`}
                        title={"Warranty"}
                        isActive={selectedSpecification === SPECIFICATION.WARRANTY}
                        clickHandler={() => { setSelectedSpecification(SPECIFICATION.WARRANTY) }} /> */}
                    <PDPVerticalProductSpecificationListItem
                        key={`VerticalProductSpecificationListItem${SPECIFICATION.CARE_AND_MAINTENANCE}`}
                        title={texts.careAndMaintenance}
                        isActive={selectedSpecification === SPECIFICATION.CARE_AND_MAINTENANCE}
                        clickHandler={() => { setSelectedSpecification(SPECIFICATION.CARE_AND_MAINTENANCE) }} />
                    <PDPVerticalProductSpecificationListItem
                        key={`VerticalProductSpecificationListItem${SPECIFICATION.DELIVERY_AND_RETURN}`}
                        title={texts.deliveryAndReturn}
                        isActive={selectedSpecification === SPECIFICATION.DELIVERY_AND_RETURN}
                        clickHandler={() => { setSelectedSpecification(SPECIFICATION.DELIVERY_AND_RETURN) }} />
                </ul>
            </div>
        </div>
        <div className="specification-list">
            <div className="specification-list__inner">
                {selectedSpecification === SPECIFICATION.ABOUT &&
                    <>
                        {activeProductData.longDescription &&
                            <>
                                <h4>{texts.aboutTheProduct.replace('{productType}', activeModelData.ProductType)}</h4>
                                <p>{activeProductData.longDescription}</p>
                            </>
                        }
                        {activeProductData.designerDescription &&
                            <>
                                <h4>{texts.aboutTheDesigner}</h4>
                                <p>{activeProductData.designerDescription}</p>
                            </>}
                    </>
                }
                {selectedSpecification === SPECIFICATION.SPECIFICATIONS &&
                    <>
                        {(activeModelData?.imperialResourceImageUrl || activeModelData?.resourceImageUrl) &&
                            activeDimensionFilter === DIMENSION_UNIT.Metric && (
                                <img
                                    className="pdp-product-specification__image"
                                    src={activeModelData.resourceImageUrl ?? activeModelData.imperialResourceImageUrl}
                                    alt="measurement pictogram"
                                />
                            )}
                        {(activeModelData?.imperialResourceImageUrl || activeModelData?.resourceImageUrl) &&
                            activeDimensionFilter === DIMENSION_UNIT.Imperial && (
                                <img
                                    className="pdp-product-specification__image"
                                    src={activeModelData?.imperialResourceImageUrl ?? activeModelData.resourceImageUrl}
                                    alt="measurement pictogram"
                                />
                            )}

                        <div className="pdp-product-specification__table-wrapper">
                            {dimensionSpecs && dimensionSpecs.length > 0 && (<>
                                <div className="pdp-product-specification__dimension-filter">
                                    <ul>
                                        <li className={`pdp-product-specification__dimension-filter-item ${activeDimensionFilter === DIMENSION_UNIT.Metric ? "active" : ""}`}>
                                            <button onClick={() => { setActiveDimensionFilter(DIMENSION_UNIT.Metric) }}>
                                                <Text field={{ value: texts.dimensionsMetric }} tag="span" />
                                            </button>
                                        </li>
                                        <li className={`pdp-product-specification__dimension-filter-item ${activeDimensionFilter === DIMENSION_UNIT.Imperial ? "active" : ""}`}>
                                            <button onClick={() => { setActiveDimensionFilter(DIMENSION_UNIT.Imperial) }} >
                                                <Text field={{ value: texts.dimensionsImperial }} tag="span" />
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                                <table className="pdp-product-specification__table">
                                    <thead>
                                        <tr>
                                            <th className="pdp-product-specification__dimension-header">{activeModelData.ProductType}</th>
                                        </tr>
                                    </thead>
                                    {dimensionSpecs.map((item, index) => {
                                        if (item?.value[0]?.sectionId === "Dimensions" && activeDimensionFilter === DIMENSION_UNIT.Imperial) {
                                            return <Fragment key={`dimensionspec-div${index}`}></Fragment>;
                                        }
                                        if (item?.value[0]?.sectionId === "ImperialDimensions" && activeDimensionFilter === DIMENSION_UNIT.Metric) {
                                            return <Fragment key={`dimensionspec-div${index}`}></Fragment>;
                                        }

                                        return (
                                            <tbody key={`dimensionspec-div${index}`}>
                                                {item.value.map((item2, index2) => {
                                                    return (
                                                        <tr key={`specAccordionInner${index2}`}>
                                                            <th>{item2?.name?.toString() || ""}</th>
                                                            <td>
                                                                {item2?.value?.toString() || ""}{" "}
                                                                {item2?.unit?.toString() || ""}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>)
                                    })}
                                </table>
                                {specAccordions &&
                                    specAccordions.length > 0 &&
                                    specAccordions.map((item, index) => {
                                        return <table key={`specaccordions-table${index}`} className="pdp-product-specification__table">
                                            <thead>
                                                <tr>
                                                    <th className="pdp-product-specification__dimension-header">{item.key}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {item.value.map((item2, index2) => {
                                                    return (
                                                        <tr key={`specAccordionInner${index2}`}>
                                                            <th>{item2?.name?.toString() || ""}</th>
                                                            <td>
                                                                {item2?.value?.toString() || ""}{" "}
                                                                {item2?.unit?.toString() || ""}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    })}
                            </>)}
                        </div>
                    </>}
                {selectedSpecification === SPECIFICATION.DOWNLOADS &&
                    <>
                        {activeModelData.downloads &&
                            activeModelData.downloads.map((link) => {
                                return (
                                    <a
                                        key={getUniqueId()}
                                        className="pdp-product-specification__link pdp-product-specification__link--not-in-table"
                                        href={link.url}
                                    >
                                        {link.name}
                                    </a>
                                );
                            })}
                    </>}
                {selectedSpecification === SPECIFICATION.CARE_AND_MAINTENANCE && <RichText
                    field={careAndMaintenance}
                    className="pdp-product-specification__bodytext"
                />}
                {selectedSpecification === SPECIFICATION.DELIVERY_AND_RETURN && deliveryAndReturnText && 
                    <RichText className="pdp-product-specification__bodytext" field={{ value: deliveryAndReturnText }} />
                }
            </div>
        </div>
    </div>
}

const PDPVerticalProductSpecificationListItem = ({ title, isActive, clickHandler }) => {
    const classList = classNames({
        "specification-list-button": true,
        "is-active": isActive,
    });

    return <li className="specification-list-filters__item">
        <button className={classList} onClick={clickHandler}>
            <span>{title}</span>
        </button>
    </li>
}
const DIMENSION_UNIT = {
    Metric: "Metric",
    Imperial: "Imperial"
}

const SPECIFICATION = {
    ABOUT: "ABOUT",
    SPECIFICATIONS: "SPECIFICATIONS",
    CERTIFICATES: "CERTIFICATES",
    DOWNLOADS: "DOWNLOADS",
    WARRANTY: "WARRANTY",
    CARE_AND_MAINTENANCE: "CARE_AND_MAINTENANCE",
    DELIVERY_AND_RETURN: "DELIVERY_AND_RETURN"
};

export default PDPVerticalProductSpecification;